import { useMutation, useQuery } from "@tanstack/react-query";
import { getBatch, listSignatures, previewTemplateVersion, type TemplateDetail } from "@/api/client";
import { useSession } from "@/auth/session";
import { saveBlob } from "@/components/download";
import { BatchPicker } from "@/components/BatchPicker";
import { EmptyState, ErrorBanner, ToolbarButton } from "@/components/ui";
import { useObjectURL } from "@/components/useObjectURL";

/** What a preview is rendered with; the template page keeps these in its URL, the designer in its state. */
export type PreviewOptions = { signature?: string; batch?: string };

export function TemplatePreview({
  template,
  versionId,
  options,
  onOptions,
  onClose,
}: {
  template: TemplateDetail;
  versionId: string;
  options: PreviewOptions;
  onOptions: (next: PreviewOptions) => void;
  onClose: () => void;
}) {
  const { can } = useSession();
  const version = template.versions.find((v) => v.id === versionId);
  // The server refuses these without the permission, so a shared link just drops them.
  const signatureId = can("signature.read") ? options.signature : undefined;
  const batchId = can("certificate.read") ? options.batch : undefined;

  const pdf = useQuery({
    queryKey: ["template-preview", template.id, versionId, signatureId ?? "", batchId ?? ""],
    queryFn: () => previewTemplateVersion(template.id, versionId, { signatureId, batchId }),
    enabled: version !== undefined,
    // Each fetch is a fresh server render, so don't redo it on focus or remount.
    staleTime: Infinity,
  });
  // While a refetch runs (e.g. after a save) the cached PDF shows the old layout, so hide it.
  const current = pdf.isFetching || pdf.isError ? undefined : pdf.data;
  const url = useObjectURL(current);
  const download = useMutation({
    mutationFn: async () => {
      if (current && version) saveBlob(current, `${template.name} v${version.version_no} preview.pdf`);
    },
  });

  const setOptions = (next: PreviewOptions) => onOptions({ ...options, ...next });

  if (!version) {
    return (
      <div className="space-y-3 rounded-lg border border-zinc-200 bg-white p-4">
        <EmptyState title="Nothing to preview" detail="That version is not part of this template." compact />
        <ToolbarButton onClick={onClose}>Close preview</ToolbarButton>
      </div>
    );
  }

  const label = `v${version.version_no}`;
  return (
    <div className="space-y-4 rounded-lg border border-zinc-200 bg-white p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold">Preview of {label}</h2>
          <p className="mt-1 text-xs text-zinc-500">
            {batchId
              ? "The batch's first five recipients. A batch with its own signature uses it instead of the one chosen here."
              : "Sample data. A missing photo or signature is drawn as a labelled box."}
          </p>
        </div>
        <div className="flex gap-2">
          <ToolbarButton disabled={!current} onClick={() => download.mutate()}>
            Download PDF
          </ToolbarButton>
          <ToolbarButton onClick={onClose}>Close preview</ToolbarButton>
        </div>
      </div>

      <div className="flex flex-wrap gap-3">
        {can("signature.read") ? (
          <SignaturePicker value={signatureId} onChange={(signature) => setOptions({ signature })} />
        ) : null}
        {can("certificate.read") ? <PreviewBatchPicker value={batchId} onChange={(batch) => setOptions({ batch })} /> : null}
      </div>

      <ErrorBanner error={pdf.error} />
      {pdf.isFetching ? <EmptyState title="Rendering preview" detail="The server is drawing the PDF." compact /> : null}
      {url && current ? <iframe title={`Preview of ${label}`} src={url} className="h-[70vh] w-full rounded-md border border-zinc-200" /> : null}
    </div>
  );
}

const pickerClass = "mt-1 block min-w-64 rounded-md border border-zinc-300 bg-white px-3 py-2 text-sm outline-none focus:border-brand focus:ring-1 focus:ring-brand";

function SignaturePicker({ value, onChange }: { value?: string; onChange: (id: string | undefined) => void }) {
  const signatures = useQuery({ queryKey: ["signatures"], queryFn: listSignatures });
  // Only a signature with a current version can be drawn.
  const usable = (signatures.data ?? []).filter((s) => s.current_version);
  return (
    <label className="block text-sm">
      <span className="font-medium text-zinc-700">Signature</span>
      <select value={value ?? ""} onChange={(e) => onChange(e.target.value || undefined)} className={pickerClass}>
        <option value="">None (placeholder box)</option>
        {value && !usable.some((s) => s.id === value) ? <option value={value}>Signature (unnamed)</option> : null}
        {usable.map((s) => (
          <option key={s.id} value={s.id}>
            {s.role_title ? `${s.signatory_name} (${s.role_title})` : s.signatory_name}
          </option>
        ))}
      </select>
    </label>
  );
}

/** The shared picker over the batch id kept in the URL; none means sample data. */
function PreviewBatchPicker({ value, onChange }: { value?: string; onChange: (id: string | undefined) => void }) {
  const linked = useQuery({ queryKey: ["batch", value], queryFn: () => getBatch(value!), enabled: Boolean(value) });
  return (
    <div className="min-w-64">
      <BatchPicker
        value={value ? (linked.data ?? null) : null}
        onChange={(batch) => onChange(batch?.id)}
        hint="Sample data is used until you pick a batch."
      />
    </div>
  );
}
